/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Grounded in budgets-finance.md, auth-security.md, and
 * schedule-calendar.md Marketing Constraints.
 *
 * Divisions are a real entity — users scope to divisions, estimates
 * and projects carry division_id, budgets can be division-level,
 * calendar events carry division_id, and grandmaster rollups
 * aggregate across divisions.
 *
 * Known issue to respect: Grandmaster budget Line Items tab is
 * EMPTY (from budgets-finance.md Known Issues). Do NOT market
 * "drill down to any line across divisions" — that tab isn't
 * populated.
 *
 * Do NOT:
 * - Market it as enterprise / PE-backed / private-equity grade
 *   without qualification — CC is built for 3-25 person
 *   contractor teams
 * - Claim all tables are division-scoped — some aren't
 * - Reveal internal column names or cascade internals
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Building2,
  Users,
  Layers,
  DollarSign,
  BarChart3,
  Workflow,
  Briefcase,
  Calculator,
  ClipboardList,
  GitBranch,
  ShieldCheck,
  Calendar as CalendarIcon,
  TrendingUp,
  PieChart,
  Filter,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Divisions",
  title: "Run multiple divisions",
  titleHighlight: "without running separate systems.",
  description:
    "Give each division its own operating clarity while leadership still gets one company-wide view of performance, pipeline, and financial risk.",

  featureGrid: {
    title: "Everything a multi-division contractor actually needs",
    items: [
      {
        icon: Building2,
        title: "Clear Division Structure",
        description:
          "Set up divisions with clear ownership so teams know exactly where work belongs.",
      },
      {
        icon: DollarSign,
        title: "Division-Level Budget",
        description:
          "Use different budget and pricing assumptions per division instead of forcing one model on every service line.",
      },
      {
        icon: Users,
        title: "Scoped Team Members",
        description:
          "Keep visibility focused so teams see the work they are responsible for and avoid cross-division noise.",
      },
      {
        icon: Workflow,
        title: "Divisional Pipeline",
        description:
          "Track sales and estimate progress by division so performance issues are easier to pinpoint.",
      },
      {
        icon: BarChart3,
        title: "Per-Division Variance Reporting",
        description:
          "See where each division is over or under plan before problems become quarter-end surprises.",
      },
      {
        icon: Layers,
        title: "Company-Wide Rollup",
        description:
          "Leadership can monitor total company performance and then drill into a specific division when needed.",
      },
      {
        icon: CalendarIcon,
        title: "Division-Scoped Calendar",
        description:
          "Filter schedules by division so dispatch and planning meetings stay focused.",
      },
      {
        icon: GitBranch,
        title: "Division Assignment At Creation",
        description:
          "New records can inherit division context to reduce setup friction and misclassification.",
      },
      {
        icon: TrendingUp,
        title: "Divisional P&L",
        description:
          "Compare profitability by division so strategic decisions are based on real numbers.",
      },
    ],
  },

  workflow: {
    title: "How multi-division actually works in CC",
    description:
      "A practical flow from division setup to day-to-day operations and leadership reporting.",
    steps: [
      {
        label: "Create",
        title: "Define your divisions",
        description:
          "Set up divisions around how your business actually operates so reporting and ownership stay clean.",
        details: [
          "Division name and description",
          "Optional color tag for visual distinction",
          "No forced template — structure matches your business",
          "Soft-delete supported for reorganization",
        ],
      },
      {
        label: "Assign",
        title: "Assign team members to divisions",
        description:
          "Match users to the divisions they support so visibility and responsibility align with real roles.",
        details: [
          "Users can belong to one or more divisions",
          "Primary division used for auto-assignment on new records",
          "Owners and admins see every division by default",
          "Scoped access enforced through shared permission checks",
        ],
      },
      {
        label: "Budget",
        title: "Set division-level budget assumptions",
        description:
          "Give each division assumptions that fit its work mix, then let teams estimate and plan from those defaults.",
        details: [
          "Division-level budget optional (inherits from org if not set)",
          "Overhead method per division",
          "Margin targets per division",
          "Contingency and burden rates per division",
          "Per-cost-type pricing configurable per division",
        ],
      },
      {
        label: "Run",
        title: "Divisional pipeline and scoped operations",
        description:
          "Run daily workflows by division so teams stay focused and handoffs are easier to manage.",
        details: [
          "Pipeline derived from estimate status, filtered by division",
          "Project and service lists respect division scoping",
          "Calendar events tagged with division",
          "Documents scoped to division where relevant",
          "Role + division = precise visibility control",
        ],
      },
      {
        label: "Roll Up",
        title: "Company-wide view for leadership",
        description:
          "Leadership can monitor overall performance, then drill into division-level detail for targeted decisions.",
        details: [
          "Grandmaster budget rollup aggregates across divisions",
          "One-click drill from rollup to any division",
          "Variance per cost type at the grandmaster level",
          "Per-division breakdown across supported financial metrics",
          "Owner / admin visibility across everything",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: Building2,
      title: "Division Operations, Not Spreadsheet Workarounds",
      description:
        "Treat divisions as real operating units so planning, sales, and financial review stay aligned as your company grows.",
      bullets: [
        "Division is a real entity with settings and a detail page",
        "Estimates, projects, services, and invoices are scoped to their division",
        "Calendar events scoped to division",
        "Users belong to one or more divisions",
        "Division scoping enforced across supported operational modules",
      ],
    },
    {
      icon: DollarSign,
      title: "Division-Specific Financial Strategy",
      description:
        "Use different financial assumptions by division so pricing and margin targets match each business line.",
      bullets: [
        "Division-level budgets with independent assumptions",
        "Overhead method configurable per division",
        "Margin, contingency, and burden rates per division",
        "Division pricing settings take priority over company defaults",
        "Per-cost-type rates (labor / materials / equipment / subs) per division",
      ],
    },
    {
      icon: Layers,
      title: "Leadership Rollup Across Divisions",
      description:
        "Get a full-company view for executive decisions while keeping one-click access to division-level performance detail.",
      bullets: [
        "Grandmaster rollup aggregates across every division",
        "Variance per cost type at the grandmaster level",
        "One-click drill to individual division",
        "Revenue recognition respected per division policy",
        "Approved change orders update each division's budget baseline automatically",
      ],
    },
    {
      icon: ShieldCheck,
      title: "Role and Division Access Control",
      description:
        "Combine role permissions with division scope so the right people can act on the right data without unnecessary exposure.",
      bullets: [
        "Role plus division scope determines exactly what each person can see and do",
        "Role determines what actions a user can take",
        "Division determines what data they can see",
        "Owner / admin see every division by default",
        "Enforced through shared permission checks, not just UI visibility",
      ],
    },
  ],

  connections: {
    title: "How divisions connect to the rest of CC",
    items: [
      {
        icon: Calculator,
        flow: "Division → Estimate Budget Cascade",
        description:
          "New estimates inherit division-level budget assumptions automatically. Per-estimate overrides are supported without affecting the division's defaults.",
      },
      {
        icon: Briefcase,
        flow: "Division → Project / Service Scoping",
        description:
          "Projects and services inherit their parent estimate's division. Reports filter by division naturally.",
      },
      {
        icon: BarChart3,
        flow: "Division Actuals → Grandmaster Rollup",
        description:
          "Each division's actuals feed the grandmaster rollup. The owner sees the whole business; each division manager sees their own.",
      },
      {
        icon: Filter,
        flow: "Division → Team Visibility",
        description:
          "Users scoped to divisions only see the work they're allowed to. Residential estimators never see commercial's pipeline unless you let them.",
      },
      {
        icon: PieChart,
        flow: "Division → Revenue Recognition",
        description:
          "Revenue recognition policies can be set at the division level. Income budget lines light up per division under each division's chosen policy.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: DollarSign,
    },
    {
      label: "Reporting & Analytics",
      href: "/features/reporting",
      icon: BarChart3,
    },
    {
      label: "Team & Permissions",
      href: "/features/collaboration",
      icon: Users,
    },
    {
      label: "Project Management",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Estimates & Takeoffs",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "CRM",
      href: "/features/crm",
      icon: ClipboardList,
    },
  ],
};

export function DivisionsPageClient() {
  return <FeaturePageTemplate config={config} />;
}
